import { Modal } from '@klear/ui/components/modal'
import { Money } from '@klear/money'
import { KlearDate } from '@klear/date'
import { useAdvanceRecoveryMutations, useAdvanceRecoveryPreview } from '@klear/ui/hooks/use-collections'
import { Loader2 } from 'lucide-react'

type AdvanceRecoveryModalProps = {
  traderId: number
  offerId: number
  offerIdDisplay: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function AdvanceRecoveryModal({
  traderId,
  offerId,
  offerIdDisplay,
  open,
  onOpenChange,
  onSuccess
}: AdvanceRecoveryModalProps) {
  const { preview, isLoading: isPreviewLoading } = useAdvanceRecoveryPreview(traderId, offerId, open)
  const { initiateAdvanceRecovery, isInitiating } = useAdvanceRecoveryMutations()

  const handleConfirm = async () => {
    try {
      await initiateAdvanceRecovery({ traderId, offerId })
      onOpenChange(false)
      onSuccess?.()
    } catch {}
  }

  const amount = preview?.amount != null ? new Money(preview.amount) : undefined
  const amountFormatted = amount?.isPositive() ? amount.toString() : null
  const dueDateFormatted = KlearDate.parse(preview?.dueDate)?.toString('MMM D, YYYY') ?? null
  const failedPayment = preview?.eligible ? preview.failedPayment : undefined
  const failedOnFormatted = KlearDate.parse(failedPayment?.failedOn)?.toString('MMM D, YYYY') ?? null
  const availableBalance =
    preview?.eligible && preview.availableBalance != null ? new Money(preview.availableBalance) : undefined
  const shortfall =
    amount != null && availableBalance?.currency === amount.currency && availableBalance.lessThan(amount)
      ? amount.subtract(availableBalance)
      : undefined

  return (
    <Modal
      size="sm"
      className="h-auto"
      open={open}
      onOpenChange={(next) => {
        if (!next && !isInitiating) onOpenChange(false)
      }}
      title={failedPayment ? 'Retry advance recovery?' : 'Initiate advance recovery?'}
      description={
        isPreviewLoading
          ? 'Loading recovery details…'
          : preview?.eligible && amountFormatted
            ? failedPayment
              ? `Your recovery payment for offer ${offerIdDisplay}${failedOnFormatted ? ` on ${failedOnFormatted}` : ''} failed.`
              : `Return the advance for offer ${offerIdDisplay} today.`
            : (preview?.ineligibleReason ?? 'Advance recovery is not available for this offer.')
      }
      primaryText={isInitiating ? 'Scheduling…' : failedPayment ? 'Retry payment' : 'Confirm'}
      primaryDisabled={
        isPreviewLoading ||
        isInitiating ||
        preview == null ||
        !preview.eligible ||
        !amount?.isPositive() ||
        shortfall != null
      }
      secondaryText="Cancel"
      secondaryDisabled={isInitiating}
      handleSecondaryAction={() => onOpenChange(false)}
      handlePrimaryAction={() => void handleConfirm()}
    >
      {isPreviewLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking eligibility…
        </div>
      ) : preview?.eligible && amountFormatted ? (
        <div className="space-y-2 text-sm">
          {failedPayment && (
            <div className="rounded-md border border-destructive/50 bg-destructive/5 px-3 py-2 text-destructive">
              {failedPayment.reason}
            </div>
          )}
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Amount</span>
            <span className="font-medium tabular-nums">{amountFormatted}</span>
          </div>
          {dueDateFormatted != null && (
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Scheduled due date</span>
              <span>{dueDateFormatted}</span>
            </div>
          )}
          {availableBalance != null && (
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Available in your Klear Financial Account</span>
              <span className={shortfall ? 'font-medium tabular-nums text-destructive' : 'tabular-nums'}>
                {availableBalance.toString()}
              </span>
            </div>
          )}
          {shortfall ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
              {`Your Klear Financial Account is ${shortfall.toString()} short of this payment. Add funds, then come back to ${failedPayment ? 'retry' : 'confirm'}. Incoming transfers count once they have posted.`}
            </div>
          ) : (
            <p className="text-muted-foreground pt-1">
              {!failedPayment
                ? 'Your recovery payment will be collected from your Klear Financial Account today.'
                : availableBalance != null
                  ? 'Your Klear Financial Account now covers this payment. Retrying collects it today.'
                  : `Make sure your Klear Financial Account has at least ${amountFormatted} available, then retry. The payment will be collected today.`}
            </p>
          )}
        </div>
      ) : null}
    </Modal>
  )
}
